import React, { useContext, useState } from 'react';
import PageTemplate from '../../common/PageTemplate';
import { fetchOrders, deleteOrder } from './apis';
import AuthContext from '../../auth/AuthContext';
import { GridColDef } from '@mui/x-data-grid';
import { InfoOutlined } from '@mui/icons-material';
import CustomerForm from '../accounts/CustomerForm';
import { useNavigate } from 'react-router-dom';
import { Checkbox, Chip, FormControlLabel } from '@mui/material';
import { getOrderStatusColor } from '../generate-sales/constants';
import { formatCurrency } from '../../../utils/auth';
import toLower from 'lodash/toLower';
import { UserInfo } from '../../auth/types';
import { updateOrderStatus } from '../generate-sales/apis';

export const FETCH_ORDERS_QUERY_KEY = 'fetchOrders';

export type Order = {
  _id?: string;
  customer_id: string;
  initiator_id: string;
  company_id: string;
  invoice_number?: string;
  billing_address?: string;
  payment_type?: string;
  payment_status?: string;
  referrer_id?: string;
  referring_doctor_id?: string;
  approver_id?: string;
  created_at?: string;
  transaction_date?: string;
  last_updated_by?: UserInfo;
};

const COLUMNS: GridColDef<Order>[] = [
  {
    field: 'created_at',
    headerName: 'Transaction Date',
    valueGetter: (value, row) =>
      `${new Date(row.transaction_date || row.created_at || '').toLocaleDateString('en-US')}`,
    sortComparator: (v1, v2) => new Date(v1).getTime() - new Date(v2).getTime(),
    flex: 1,
  },
  {
    field: 'invoice_number',
    headerName: 'Invoice ID',
    flex: 1,
  },
  {
    field: 'customer_name',
    headerName: 'Customer Name',
    minWidth: 300,
    flex: 1,
    // eslint-disable-next-line
    valueGetter: (value, row) => (row?.customer_id as any)?.customer_name,
  },
  {
    field: 'net_total',
    headerName: 'Total Amount',
    flex: 1,
    valueGetter: (value) => formatCurrency(value || 0),
  },
  {
    field: 'payment_type',
    headerName: 'Payment Type',
    flex: 1,
    valueGetter: (value) => ((value || '') as string).toUpperCase(),
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    // eslint-disable-next-line
    renderCell: ({ row }: any) => (
      <Chip
        label={row.status?.toUpperCase()?.replaceAll('_', ' ')}
        color={getOrderStatusColor(row.status)}
      />
    ),
  },
  {
    field: 'initiator_id',
    headerName: 'Transacted By',
    flex: 1,
    valueGetter: (value, row) =>
      // eslint-disable-next-line
      `${(row?.initiator_id as any)?.first_name} ${(row?.initiator_id as any)?.last_name}`,
  },
  {
    field: 'last_updated_by',
    headerName: 'Last Updated By',
    flex: 1,
    valueGetter: (value, row) =>
      // eslint-disable-next-line
      `${(row?.last_updated_by as any)?.first_name || ''} ${(row?.last_updated_by as any)?.last_name || ''}`,
  },
];

export default function Orders() {
  const { activeCompany } = useContext(AuthContext);
  const navigate = useNavigate();
  const [hideEmptyInvoices, setHideEmptyInvoices] = useState(true);

  const renderCheckbox = () => (
    <FormControlLabel
      control={
        <Checkbox
          checked={hideEmptyInvoices}
          onChange={() => setHideEmptyInvoices(!hideEmptyInvoices)}
          color="success"
          sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
        />
      }
      label={'Hide Empty Invoices'}
    />
  );

  return (
    <PageTemplate
      fetchAPI={fetchOrders}
      // eslint-disable-next-line
      archiveAPI={(o: any) => updateOrderStatus({ ...o, status: 'archived' })}
      fetchParams={{
        company_id: activeCompany?._id as string,
        hideEmptyInvoices,
      }}
      // eslint-disable-next-line
      viewItem={(item: any) => navigate(`/orders/${item._id}`)}
      queryKey={FETCH_ORDERS_QUERY_KEY}
      itemName="order"
      searchPlaceholder="Search by invoice number or customer name..."
      searchAttr="invoice_number"
      searchFunc={(data: Order[], value: string) => {
        const filtered = data.filter(
          ({ invoice_number, customer_id }) =>
            toLower(invoice_number).includes(toLower(value)) ||
            // eslint-disable-next-line
            toLower((customer_id as any)?.customer_name).includes(
              toLower(value),
            ),
        );
        return filtered;
      }}
      sortField="invoice_number"
      sortDir="desc"
      columns={COLUMNS}
      menuActions={['View', 'Archive']}
      drawerTabs={[
        {
          label: 'Info',
          icon: <InfoOutlined />,
          Component: CustomerForm,
        },
      ]}
      renderCustomComponent={renderCheckbox}
    />
  );
}
